import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { ATTRIBUTE_ICONS } from '@/modules/CRM/components/CRMRecordSidebar';

const ATTRIBUTE_TYPES = [
    { type: 'text', label: 'Short Text', desc: 'Single-line plain text for names or IDs' },
    { type: 'number', label: 'Number', desc: 'Integers, decimals, or metric counters' },
    { type: 'checkbox', label: 'Checkbox', desc: 'Boolean toggle (True or False)' },
    { type: 'date', label: 'Date', desc: 'Standard date calendar picker' },
    { type: 'rating', label: 'Rating', desc: 'Star rating indicators (1-5 stars)' },
    { type: 'select', label: 'Select', desc: 'Single option selection dropdown' },
    { type: 'currency', label: 'Currency', desc: 'Formatted dollar / monetary values' },
    { type: 'location', label: 'Location', desc: 'Country or physical address' },
    { type: 'phone', label: 'Phone Number', desc: 'Click-to-call direct phone numbers' },
    { type: 'domain', label: 'Domain / URL', desc: 'Clickable website links' }
];

export default function CreateAttributeModal({ isOpen, onClose, recordType }) {
    const [selectedType, setSelectedType] = useState('text');
    const [label, setLabel] = useState('');
    const [optionsInput, setOptionsInput] = useState('');

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!label.trim()) return;

        // Create the new attribute configuration
        const newAttr = {
            id: label.toLowerCase().replace(/[^a-z0-9]/g, '_'),
            label: label.trim(),
            type: selectedType,
            category: 'Custom'
        };

        if (selectedType === 'select' && optionsInput.trim()) {
            newAttr.options = optionsInput.split(',').map(s => s.trim()).filter(Boolean);
        }

        // Fetch current custom attributes from LocalStorage
        const currentSaved = localStorage.getItem(`crm_custom_attrs_${recordType}`);
        const list = currentSaved ? JSON.parse(currentSaved) : [];
        
        // Save back
        localStorage.setItem(`crm_custom_attrs_${recordType}`, JSON.stringify([...list, newAttr]));

        // Dispatch a custom event to notify active pages
        window.dispatchEvent(new Event('crm_custom_attributes_changed'));

        // Reset
        setLabel('');
        setSelectedType('text');
        setOptionsInput('');
        onClose();
    };

    return (
        <div style={backdropStyle} onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 16 }}
                transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                style={modalStyle}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={headerStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={iconContainerStyle}>
                            <ObliqueIcon name="add_circle" size={18} color="var(--accent)" />
                        </div>
                        <div>
                            <h2 style={titleStyle}>Create dynamic attribute</h2>
                            <span style={subtitleStyle}>Layer a custom data property onto {recordType} records</span>
                        </div>
                    </div>
                    <button style={closeBtnStyle} onClick={onClose}>
                        <ObliqueIcon name="close" size={20} color="var(--text-secondary)" />
                    </button>
                </div>

                {/* Form Body */}
                <form onSubmit={handleSubmit} style={formStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        
                        {/* Name / Label */}
                        <div>
                            <label style={labelStyle}>Attribute Label</label>
                            <input
                                type="text"
                                placeholder="e.g. Lead Quality, Funding Round"
                                required
                                value={label}
                                onChange={e => setLabel(e.target.value)}
                                style={inputStyle}
                                autoFocus
                            />
                        </div>

                        {/* Attribute Type Selector Grid */}
                        <div>
                            <label style={labelStyle}>Data Type</label>
                            <div style={gridStyle}>
                                {ATTRIBUTE_TYPES.map(t => {
                                    const isSelected = selectedType === t.type;
                                    return (
                                        <div
                                            key={t.type}
                                            onClick={() => setSelectedType(t.type)}
                                            style={cardStyle(isSelected)}
                                        >
                                            <div style={cardHeaderStyle(isSelected)}>
                                                <ObliqueIcon name={ATTRIBUTE_ICONS[t.type] || 'description'} size={15} color={isSelected ? 'var(--accent)' : 'var(--text-secondary)'} />
                                                <span style={cardTitleStyle(isSelected)}>{t.label}</span>
                                            </div>
                                            <p style={cardDescStyle}>{t.desc}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Options Input (Only visible for Select type) */}
                        {selectedType === 'select' && (
                            <motion.div
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                style={{ background: 'var(--bg-subtle)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)' }}
                            >
                                <label style={labelStyle}>Select Options (Comma-separated)</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Tier 1, Tier 2, Tier 3"
                                    value={optionsInput}
                                    onChange={e => setOptionsInput(e.target.value)}
                                    style={inputStyle}
                                    required
                                />
                            </motion.div>
                        )}
                    </div>

                    {/* Footer buttons */}
                    <div style={footerStyle}>
                        <button type="button" className="btn btn-secondary" onClick={onClose} style={{ height: '36px' }}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" style={{ height: '36px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <ObliqueIcon name="check" size={16} />
                            Create Attribute
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}

// ─── STYLES ─────────────────────────────────────────

const backdropStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(4px)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const modalStyle = {
    width: '640px',
    maxWidth: '90%',
    background: 'var(--bg-surface)',
    borderRadius: '16px',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
};

const headerStyle = {
    padding: '20px 24px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg-subtle)'
};

const iconContainerStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'var(--accent-subtle)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'inset 0 0 0 1px var(--accent-border)'
};

const titleStyle = {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--text-primary)'
};

const subtitleStyle = {
    fontSize: '11px',
    color: 'var(--text-tertiary)'
};

const closeBtnStyle = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const formStyle = {
    padding: '24px',
    flex: 1
};

const labelStyle = {
    display: 'block',
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px'
};

const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    fontSize: '13px',
    color: 'var(--text-primary)',
    outline: 'none',
    boxShadow: 'none'
};

const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    maxHeight: '260px',
    overflowY: 'auto',
    padding: '4px'
};

const cardStyle = (selected) => ({
    padding: '10px 14px',
    borderRadius: '8px',
    border: selected ? '2px solid var(--accent)' : '1px solid var(--border)',
    background: selected ? 'var(--accent-subtle)' : 'var(--bg-surface)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    transition: 'all 0.15s ease'
});

const cardHeaderStyle = (selected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: selected ? '600' : '500',
    color: selected ? 'var(--accent)' : 'var(--text-primary)'
});

const cardTitleStyle = (selected) => ({
    fontSize: '13px',
    fontWeight: 600
});

const cardDescStyle = {
    margin: 0,
    fontSize: '11.5px',
    color: 'var(--text-tertiary)',
    lineHeight: '1.3'
};

const footerStyle = {
    marginTop: '28px',
    borderTop: '1px solid var(--border-subtle)',
    paddingTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
};
